import { ChevronDown, ChevronRight } from "lucide-react";
import React from "react";

import type {
  DashboardSummary,
  OrganizationQty,
  WarehouseQty,
} from "@/api/warehouse_dashboard";
import { ruNum } from "@/lib/format";
import { cn } from "@/lib/utils";
import { fmtRubExact } from "@/lib/warehouse-overview";

import { DashboardWarnings } from "./DashboardWarnings";
import { fmtCost, NUM, ShareCell, UNITS, UNITS_TITLE } from "./stock-cells";
import { StockItemRows } from "./StockItemRows";

/**
 * Вкладка «Остатки»: юрлицо → склад → позиция → партии (каждый уровень
 * раскрывается), одной плотной таблицей; позиции склада — отдельный запрос
 * при раскрытии (`StockItemRows`).
 * Ширина — вся колонка страницы (`max-w-[1400px]` оболочки): с раскрытием до
 * позиций длинным названиям номенклатуры нужно место (при 880px они
 * обрезались до нечитаемого — жалоба владельца 26.09.2026). Главное число — ₽ (себестоимость из учётного `Запасы`):
 * единицы разных товаров (шт, кг, л) складываются только условно, поэтому они
 * вторичной колонкой «ед.». Доля — по ₽ от итога ответа (`total_cost_rub`);
 * фронт только делит для показа, ничего не пересчитывает и не пересортировывает.
 */
export function StockTab({ data }: { data: DashboardSummary }): React.ReactElement {
  // Раскрытые юрлица — состояние ЭКРАНА, не настройка: дашборд открывают,
  // чтобы посмотреть, и вчерашний набор раскрытых строк тут не нужен.
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const toggle = (ref: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });

  return (
    <div className="space-y-2">
      <DashboardWarnings warnings={data.warnings} />
      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="w-full min-w-[640px] table-fixed text-[13px] leading-5">
          <colgroup>
            <col />
            <col className="w-[104px]" />
            <col className="w-[136px]" />
            <col className="w-[112px]" />
            <col className="w-[160px]" />
          </colgroup>
          <thead className="border-b bg-slate-50 text-xs text-muted-foreground">
            <tr className="text-left">
              <th className="px-2 py-1.5 pl-3 font-medium">Юрлицо / склад</th>
              <th className="px-2 py-1.5 font-medium">Тип</th>
              <th className="px-2 py-1.5 text-right font-medium">Остаток, ₽</th>
              <th className="px-2 py-1.5 text-right font-medium" title={UNITS_TITLE}>
                Остаток, ед.
              </th>
              <th className="px-2 py-1.5 pr-3 font-medium">Доля от итого</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b bg-slate-50/80 font-semibold">
              <td className="px-2 py-1 pl-3">
                Итого{" "}
                <span className="text-xs font-normal text-muted-foreground">
                  {ruNum.format(data.organizations.length)} юрл.
                </span>
              </td>
              <td />
              <td className={cn(NUM, "text-sm")}>{fmtRubExact(data.total_cost_rub)}</td>
              <td className={UNITS} title={UNITS_TITLE}>
                {ruNum.format(data.total_qty)}
              </td>
              <td />
            </tr>
            {data.organizations.map((org) => (
              <OrganizationRows
                key={org.ref}
                org={org}
                total={data.total_cost_rub}
                open={expanded.has(org.ref)}
                onToggle={() => toggle(org.ref)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrganizationRows({
  org,
  total,
  open,
  onToggle,
}: {
  org: OrganizationQty;
  total: number;
  open: boolean;
  onToggle: () => void;
}): React.ReactElement {
  return (
    <>
      <tr className={cn("border-b hover:bg-slate-50", open && "bg-slate-50")}>
        <td className="px-2 py-1 pl-3">
          <button
            type="button"
            aria-expanded={open}
            onClick={onToggle}
            title={org.name}
            className="flex w-full min-w-0 items-center gap-1 text-left"
          >
            {open ? (
              <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
            )}
            <span className="truncate font-medium">{org.name}</span>
            <span className="shrink-0 text-xs text-muted-foreground">
              {ruNum.format(org.warehouses.length)} скл.
            </span>
          </button>
        </td>
        <td />
        <td className={cn(NUM, "font-medium")}>{fmtCost(org.cost_rub)}</td>
        <td className={UNITS}>{ruNum.format(org.qty)}</td>
        <ShareCell cost={org.cost_rub} total={total} strong />
      </tr>
      {open &&
        org.warehouses.map((wh) => (
          <WarehouseRows key={wh.ref} orgRef={org.ref} wh={wh} total={total} />
        ))}
    </>
  );
}

function WarehouseRows({
  orgRef,
  wh,
  total,
}: {
  orgRef: string;
  wh: WarehouseQty;
  total: number;
}): React.ReactElement {
  // Позиции склада грузятся только раскрытием: это отдельный поход в 1С.
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <tr className={cn("border-b bg-slate-50/50 hover:bg-slate-100/60", open && "bg-slate-100/60")}>
        {/* Вложенность — линией слева, не отступом: ширина нужна числам. */}
        <td className="py-0 pl-3 pr-2">
          <div className="ml-1.5 border-l-2 border-slate-300 pl-2">
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              title={wh.name}
              className="flex w-full min-w-0 items-center gap-1 py-1 text-left"
            >
              {open ? (
                <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
              )}
              <span className="truncate">{wh.name}</span>
            </button>
          </div>
        </td>
        <td className="truncate px-2 py-1 text-xs text-muted-foreground">{wh.kind}</td>
        <td className={NUM}>{fmtCost(wh.cost_rub)}</td>
        <td className={UNITS}>{ruNum.format(wh.qty)}</td>
        <ShareCell cost={wh.cost_rub} total={total} />
      </tr>
      {open && <StockItemRows pair={{ organization_ref: orgRef, warehouse_ref: wh.ref }} />}
    </>
  );
}
